import { useFlag } from '@unleash/proxy-client-react'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import { IconButton, Tooltip, useTheme } from '@mui/material'
import { useParams } from 'react-router-dom'
import { activeAlbumIdVar, drawerVar, modalVar } from '../../../../../providers/apollo/cache'
import useSelectedAlbums from '../../../hooks/useSelectedAlbums'
import useBucket from '../../../hooks/useBucket'
import useBucketMigrationProgress from '../../../hooks/useBucketMigrationProgress'
import useBucketConvertProgress from '../../../hooks/useBucketConvertProgress'

export default function AlbumMoveIcon() {
  const { bucketId, albumId } = useParams<{ bucketId: string; albumId: string }>()
  const useUpdatedAlbumActions = useFlag('useUpdatedAlbumActions')
  const { selectedAlbums } = useSelectedAlbums()
  const containRootAlbum = Array.from(selectedAlbums.keys()).includes(bucketId as string)
  const { bucketUser, isFreemiumBucket, currentUserIsOwner } = useBucket()
  const albumMovePermission = bucketUser?.permissions?.albumUpdate
  const isMigrationInProgressForBucket = useBucketMigrationProgress()
  const isBucketConvertInProgressForBucket = useBucketConvertProgress()
  const theme = useTheme()

  let title = 'Move Album'
  if (isMigrationInProgressForBucket || isBucketConvertInProgressForBucket) {
    title = 'Move album is disabled during the migration process'
  } else if (!albumMovePermission) {
    title = 'Your role for this bucket does not allow you to move albums.'
  }

  const disabled =
    containRootAlbum ||
    !(albumId || selectedAlbums.size === 1) ||
    !albumMovePermission ||
    isMigrationInProgressForBucket ||
    isBucketConvertInProgressForBucket
  const freemiumRestricted = isFreemiumBucket && !disabled

  const handleClick = () => {
    if (!albumId && selectedAlbums.size < 1) return

    if (isFreemiumBucket) {
      modalVar(currentUserIsOwner ? 'upsellModal' : 'premiumFeature')
      return
    }

    if (albumId) {
      activeAlbumIdVar(albumId)
    } else if (selectedAlbums.size > 0) {
      const firstAlbumId = Array.from(selectedAlbums.keys())[0]
      activeAlbumIdVar(firstAlbumId)
    }
    drawerVar('bucketAlbumMove')
  }

  return (
    <Tooltip title={title} arrow>
      <IconButton
        color={freemiumRestricted ? 'groupsy700' : 'primary'}
        aria-label="Move Album"
        disabled={disabled}
        onClick={handleClick}
        id="move-media"
        data-test="move-media"
        sx={{
          padding: useUpdatedAlbumActions ? '4px' : '8px',
          bgcolor: freemiumRestricted ? theme.palette.groupsyPink[50] : 'inherit',
          '&:hover': () =>
            freemiumRestricted
              ? {
                  bgcolor: theme.palette.groupsyPink[50],
                  cursor: 'default'
                }
              : {}
        }}
      >
        <DriveFileMoveOutlinedIcon />
      </IconButton>
    </Tooltip>
  )
}
